/* Symbolic constant -> lodash dot-path into the Context state tree. Every table mutates through
   `set(cloneDeep(state), PATH.KEY, value)`, so this module is the single binding between UI fields
   and the deeply nested costing state.

   Return types are inferred rather than annotated: the inferred object literal gives call sites
   key-level autocomplete and turns a mistyped constant into a compile error, which a
   `Record<string, string>` annotation would erase.

   Ported 1:1 from NewProductCosting/fieldPath.js. The `grandTotal(index)` factory is deliberately
   NOT carried over — it had zero importers in the entire repo. */

/** Index arguments are interpolated into the paths, so every factory accepts them optionally. */
type RowIndex = number | undefined;

export const averageCostingFieldpath = (index?: RowIndex, chIndex?: RowIndex) => {
  return {
    COLOURWAY_TOTAL: 'averageCosting.colourwayTot',
    SUM_QUANTITY_PER_SIZ_COl: 'averageCosting.sumOfQuantityPerSizCol',
    PRICE_PER_UNIT_TOTAL: 'averageCosting.totalPricePerUnit',
    SELECTED_COLOURWAY: `averageCosting.averageCostingInfo[${index}].colourway`,
    SELECTED_SIZE: `averageCosting.averageCostingInfo[${index}].size`,
    SELECTED_LENGTH: `averageCosting.averageCostingInfo[${index}].length`,
    QUANTITY_PER_SIZCOL: `averageCosting.averageCostingInfo[${index}].quantityPerSizCol`,
    PRICE_PER_UNIT: `averageCosting.averageCostingInfo[${index}].pricePerUnit`,
    LENGTH_TOTAL: 'averageCosting.lengthTot',
    SIZE_TOTAL: 'averageCosting.sizeTot',
    AVERAGE_COSTING_INFO: 'averageCosting.averageCostingInfo',
    AVERAGE_COSTING_ACTIVE: 'averageCosting.averageCostingActive',
    SUM_TOTAL_PRICE: 'averageCosting.sumTotalPrice',
    EXPAND_QUANTITY_PER_SIZ_COL: `averageCosting.averageCostingInfo[${index}].expandData[${chIndex}].quantityPerSizCol`,
    EXPAND_PRICE_PER_UNIT: `averageCosting.averageCostingInfo[${index}].expandData[${chIndex}].pricePerUnit`,
    EXPAND_TOTAL_PRICE: `averageCosting.averageCostingInfo[${index}].expandData[${chIndex}].totalPricePerUnit`,
    EXPAND_SUM_TOTAL_PRICE: `averageCosting.averageCostingInfo[${index}].sumTotalPrice`,
    EXPAND_SUM_QUANTITY_PER_COL_SIZ: `averageCosting.averageCostingInfo[${index}].sumOfQuantityPerSizCol`,
    EXPAND_SUM_PRICE_PER_UNIT: `averageCosting.averageCostingInfo[${index}].sumOfPricePerUnit`,
    EXPAND_OVERALL_SUM_TOTAL_PRICE: 'averageCosting.overAllSumOfTotalPrice',
    EXPAND_OVERALL_SUM_QUANTITY_PER_COL_SIZ: 'averageCosting.overAllSumOfQuantityPerSizCol',
    EXPAND_OVERALL_SUM_PRICE_PER_UNIT: 'averageCosting.overAllSumOfPricePerUnit',
    IS_CHANGED_BY_SUPPLIER_PRICE_PER_UNIT: `averageCosting.averageCostingInfo[${index}].expandData[${chIndex}].isChangedBySupllierPricePerUnit`,
    MULTI_QUANTITY_ROWS: `averageCosting.averageCostingInfo[${index}].expandData[${chIndex}].multiQuantityRows`,
    LOADER: 'averageCosting.loader',
    TOTAL_AVERAGE_COLOURWAYS: 'averageCosting.totColourways',
    TOTAL_AVERAGE_SIZES: 'averageCosting.totSize',
    TOTAL_AVERAGE_LENGTH: 'averageCosting.totLength',
  };
};

/* `_expIndex` is accepted but never interpolated — callers pass three indices, the original only
   ever used the first two. Kept on the arity so existing call sites are unchanged. */
export const detailedCostingFieldpath = (index?: RowIndex, chIndex?: RowIndex, _expIndex?: RowIndex) => {
  return {
    TOTAL_QUANTITY: `detailedCosting.bomTypeArr[${index}].totalQuantity`,
    TOTAL_PRICE: `detailedCosting.bomTypeArr[${index}].totalPricePerUnit`,
    SELECTED_COLOURWAY: `detailedCosting.bomTypeArr[${index}].colourway`,
    SELECTED_SIZE: `detailedCosting.bomTypeArr[${index}].size`,
    SELECTED_LENGTH: `detailedCosting.bomTypeArr[${index}].length`,
    QUANTITY_PER_UNIT: `detailedCosting.bomTypeArr[${index}].quantityPerSizCol`,
    PRICE_PER_UNIT: `detailedCosting.bomTypeArr[${index}].pricePerUnit`,
    UOM_ONE_SELECTED: `detailedCosting.bomTypeArr[${index}].quantityUom`,
    UOM_TWO_SELECTED: `detailedCosting.bomTypeArr[${index}].totalQuantityUom`,
    DETAILED_COSTING_ACTIVE: 'detailedCosting.detailedCostingActive',
    DETAILED_COSTING_DATA: 'detailedCosting.bomTypeArr',
    COLOUR_REF: `detailedCosting.bomTypeArr[${index}].colourRef`,
    COLOUR_REF_OPTION: `detailedCosting.bomTypeArr[${index}].colourRefOptions`,
    SUM_TOTAL_PRICE: 'detailedCosting.sumTotalPrice',
    SUM_TOTAL_QUANTITY: `detailedCosting.bomTypeArr[${index}].sumOfTotalQuantity`,
    LENGTH_TOTAL: `detailedCosting.bomTypeArr[${index}].lengthTot`,
    SIZE_TOTAL: `detailedCosting.bomTypeArr[${index}].sizeTot`,
    COLOURWAY_TOTAL: `detailedCosting.bomTypeArr[${index}].colourwayTot`,
    SUM_QUANTITY_PER_SIZ_COl: `detailedCosting.bomTypeArr[${index}].sumOfQuantityPerSizCol`,
    PRICE_PER_UNIT_TOTAL: `detailedCosting.bomTypeArr[${index}].totalPricePerUnit`,
    COPY_DATA: `detailedCosting.bomTypeArr[${index}].`,
    IS_CHANGED_BY_SUPPLIER_PRICE_PER_UNIT: `detailedCosting.bomTypeArr[${index}].expandData[${chIndex}].isChangedBySupplierPricePerUnit`,
    IS_CHANGED_BY_SUPPLIER_QUANTITY_UOM: `detailedCosting.bomTypeArr[${index}].expandData[${chIndex}].isChangedBySupplierQuantityUom`,
    IS_CHANGED_BY_SUPPLIER_QUANTITY_PER_SIZE_COl: `detailedCosting.bomTypeArr[${index}].expandData[${chIndex}].isChangedBySupplierTotalQuantityPerSizCol`,
    IS_CHANGED_BY_SUPPLIER_TOTAL_PRICE: `detailedCosting.bomTypeArr[${index}].expandData[${chIndex}].isChangedBySupplierTotalPrice`,
    EXPAND_QUANTITY_PER_SIZ_COL: `detailedCosting.bomTypeArr[${index}].expandData[${chIndex}].quantityPerSizCol`,
    EXPAND_PRICE_PER_UNIT: `detailedCosting.bomTypeArr[${index}].expandData[${chIndex}].pricePerUnit`,
    EXPAND_QUANTITY_PER_SIZ_COL_UOM: `detailedCosting.bomTypeArr[${index}].expandData[${chIndex}].quantityUom`,
    EXPAND_TOTAL_PRICE: `detailedCosting.bomTypeArr[${index}].expandData[${chIndex}].totalPrice`,
    EXPAND_SUM_TOTAL_PRICE: `detailedCosting.bomTypeArr[${index}].sumTotalPrice`,
    EXPAND_SUM_QUANTITY_PER_COL_SIZ: `detailedCosting.bomTypeArr[${index}].sumOfQuantityPerSizCol`,
    EXPAND_SUM_PRICE_PER_UNIT: `detailedCosting.bomTypeArr[${index}].sumOfPricePerUnit`,
    EXPAND_COLOUR_REF: `detailedCosting.bomTypeArr[${index}].expandData[${chIndex}].colourRef`,
    BOM_QUANTITY_UOM: `detailedCosting.bomTypeArr[${index}].expandData[${chIndex}].quantityUnitOfMeasure`,
    BOM_DETAILED_COSTING_DATA: `detailedCosting.bomTypeArr[${index}].detailedCostingInfo`,
    IS_EXCLUDED: `detailedCosting.bomTypeArr[${index}].expandData[${chIndex}].isExcluded`,
  };
};

export const testingFieldpath = (index?: RowIndex) => {
  return {
    TOTAL_PRICE: `testing.testingInfo[${index}].totalPrice`,
    SELECTED_COLOURWAY: `testing.testingInfo[${index}].colourway`,
    SELECTED_TEST_CODE: `testing.testingInfo[${index}].testingCode`,
    TESTING_DESC: `testing.testingInfo[${index}].testingDesc`,
    TESTING_TYPE: `testing.testingInfo[${index}].testingType`,
    IS_EXCLUDED: `testing.testingInfo[${index}].isExcluded`,
    SUM_PRICE: 'testing.sumOfPrice',
    TESTING_INFO_DATA: 'testing.testingInfo',
    COLOUR_REF: `testing.testingInfo[${index}].colourRef`,
    COLOUR_REF_OPTION: `testing.testingInfo[${index}].colourRefOptions`,
    BOM_COMPONENT: `testing.testingInfo[${index}].bomComponent`,
    IS_CHANGED_BY_SUPPLIER_TOTAL_PRICE: `testing.testingInfo[${index}].isChangedBySupplierTotalPrice`,
    IS_CHANGED_BY_SUPPLIER_PRICE_PER_UNIT: `testing.testingInfo[${index}].isChangedBySupplierPricePerUnit`,
    PRICE_PER_UNIT: `testing.testingInfo[${index}].pricePerUnit`,
    COPY_DATA: `testing.testingInfo[${index}]`,
  };
};

export const additionalFieldPath = (index?: RowIndex) => {
  return {
    SELECTED_TEMP_ADDTIONAL_DATA: 'additional.additionalInfo',
    SELECTED_COLOURWAY: `additional.additionalInfo[${index}].colourway`,
    UOM_ONE_SELECTED: `additional.additionalInfo[${index}].quantityUom`,
    UOM_TWO_SELECTED: `additional.additionalInfo[${index}].totalQuantityUom`,
    TOTAL_PRICE: `additional.additionalInfo[${index}].totalPrice`,
    TOTAL_QUANTITY: `additional.additionalInfo[${index}].totalQuantity`,
    QUANTITY_PER_UNIT: `additional.additionalInfo[${index}].quantityPerColSizLen`,
    PRICE_PER_UNIT: `additional.additionalInfo[${index}].pricePerUoM`,
    ITEM: `additional.additionalInfo[${index}].title`,
    IS_CHANGED_BY_SUPPLIER_COLOURWAY: `additional.additionalInfo[${index}].isChangedBySupplierColourway`,
    IS_CHANGED_BY_SUPPLIER_QUANTITY_UOM: `additional.additionalInfo[${index}].isChangedBySupplierQuantityUom`,
    IS_CHANGED_BY_SUPPLIER_TOTAL_QUANTITY_UOM: `additional.additionalInfo[${index}].isChangedBySupplierTotalQuantityUom`,
    IS_CHANGED_BY_SUPPLIER_TOTAL_PRICE: `additional.additionalInfo[${index}].isChangedBySupplierTotalPrice`,
    IS_CHANGED_BY_SUPPLIER_TOTAL_QUANTITY: `additional.additionalInfo[${index}].isChangedBySupplierTotalQuantity`,
    IS_CHANGED_BY_SUPPLIER_QUANTITY_PER_SIZE_COl: `additional.additionalInfo[${index}].isChangedBySupplierTotalQuantityPerSizColLen`,
    IS_CHANGED_BY_SUPPLIER_PRICE_PER_UOM: `additional.additionalInfo[${index}].isChangedBySupplierPricePerUom`,
    IS_CHANGED_BY_SUPPLIER_TITLE: `additional.additionalInfo[${index}].isChangedBySupplierTitle`,
    IS_CHANGED_BY_SUPPLIER_PRICE_PER_UNIT: `additional.additionalInfo[${index}].isChangedBySupplierPricePerUnit`,
    IS_EXCLUDED: `additional.additionalInfo[${index}].isExcluded`,
    SUM_TOTAL_PRICE: 'additional.sumOfTotPrice',
  };
};

export const landedInfoFieldPath = (index?: RowIndex, chIndex?: RowIndex, aliasName?: string) => {
  return {
    SELECTED_COLOURWAY: `landedInfo.landedInfoArr[${index}].colourway`,
    SELLING_PRICE: `landedInfo.landedInfoArr[${index}].sellingPrice`,
    VAT: `landedInfo.landedInfoArr[${index}].vat`,
    LANDED_INFO_DATA: 'landedInfo.landedInfoArr',
    LANDED_DYNAMIC_COL_VALUE: `landedInfo.landedInfoArr[${index}].landedInfoRowArr[${chIndex}].colValue`,
    SELECTED_SIZE: `landedInfo.landedInfoArr[${index}].size`,
    LANDED_COST: `landedInfo.landedInfoArr[${index}].landedCost`,
    GROSS_PROFIT: `landedInfo.landedInfoArr[${index}].profit`,
    MARGIIN_PERCENT: `landedInfo.landedInfoArr[${index}].marginPercent`,
    LANDED_INFO_DYNAMIC_COLUMN: 'landedInfo.landedInfoColumnArr',
    MARKUP: `landedInfo.landedInfoArr[${index}].markup`,
    LANDED_INFO_SELECTED_CURRENCY: 'landedInfo.currency',
    SELECTED_LENGTH: `landedInfo.landedInfoArr[${index}].length`,
    LANDED_INFO_VAT: 'landedInfo.vat',
    LANDEDINFO_CURRENCY_ARR: 'landedInfo.productCurreArr',
    LANDED_LANDEDINFO_DYNAMIC_COL_VALUE: `landedInfo.landedInfoArr[${index}].landedInfo[${chIndex}].tempValue`,
    LANDED_DEFAULT_COL_VALUE: `landedInfo.landedInfoArr[${index}].${aliasName}`,
    LANDED_BOTTOM_COL_VALUE: `landedInfo.landedInfoArr[${index}].${aliasName}`,
  };
};

export const productInfoFieldPath = () => {
  return {
    SELECTED_SUPPLIER: 'productInfo.supplierId',
    SELECTED_CURRENCY: 'productInfo.currency',
    SELECTED_INCOTERMS: 'productInfo.incoterms',
    REFERENCE: 'productInfo.reference',
    SELECTED_SHIPMENT_METHOD: 'productInfo.shipmentMethod',
    TARGET_COST: 'productInfo.targetCost',
    SELECTED_FACTORY: 'productInfo.factoryId',
    MOQ_NEEDED: 'productInfo.moqNeeded',
  };
};

export const commonPath = () => {
  return {
    COLOURWAY_OPTIONS: 'colourwayOptionsArr',
    COLOURWAY_REF_OPTIONS: 'colourRefOptionsArr',
    LENGTH_OPTIONS: 'lengthOptionsArr',
    SIZE_OPtIONS: 'sizeOptionsArr',
    UOM_OPTIONS: 'uomOptionsArr',
    SUPPLIER_OPTIONS: 'supplierOptionsArr',
    TEST_CODE_OPTIONS: 'testCodeOptionsArr',
    PRODUCT_INFO: 'productInfo',
    STATUS: 'status',
    SELECTED_TEMPLATE: 'productTemplate',
    PRODUCT_ID: 'productId',
    ISBREAKDOWN: 'isBreakdown',
    SELECTED_SAMPLE_REF: 'sampleRef',
    ISBREAKDOWNMANUALTOGGLE: 'isBrekdownManualToggle',
    LANDED_INFORMATION_COLUMNS: 'landedInformationColumns',
  };
};

export const quantitiesFieldpath = (index?: RowIndex, chIndex?: RowIndex) => {
  return {
    COLOURWAY_TOTAL: 'quantities.colourwayTot',
    SUM_QUANTITY_PER_SIZ_COl: 'quantities.sumOfQuantityPerSizCol',
    PRICE_PER_UNIT_TOTAL: 'quantities.totalPricePerUnit',
    SELECTED_COLOURWAY: `quantities.quantitiesInfo[${index}].colourway`,
    SELECTED_SIZE: `quantities.quantitiesInfo[${index}].size`,
    SELECTED_LENGTH: `quantities.quantitiesInfo[${index}].length`,
    QUANTITY_PER_SIZCOL: `quantities.quantitiesInfo[${index}].quantityPerSizCol`,
    LENGTH_TOTAL: 'quantities.lengthTot',
    SIZE_TOTAL: 'quantities.sizeTot',
    QUANTITES_INFO: 'quantities.quantitiesInfo',
    TOTAL_QUANTITY: `quantities.quantitiesInfo[${index}].totalQuantity`,
    SUM_TOTAL_PRICE: 'quantities.sumTotalPrice',
    SUM_TOTAL_QUANTITY: 'quantities.sumOfTotalQuantity',
    EXPAND_OVERALL_SUM_QUANTITY_PER_COL_SIZ: 'quantities.overAllSumOfQuantityPerSizCol',
    EXPAND_QUANTITY_PER_SIZCOL: `quantities.quantitiesInfo[${index}].expandData[${chIndex}].quantityPerSizCol`,
    TOTAL_AVERAGE_COLOURWAYS: 'quantities.totColourways',
    TOTAL_AVERAGE_SIZES: 'quantities.totSize',
    TOTAL_AVERAGE_LENGTH: 'quantities.totLength',
  };
};

export const errorpath = (index?: RowIndex, chIndex?: RowIndex, exIndex?: RowIndex) => {
  return {
    AVERAGE: {
      ERROR_PRICE_PER_UNIT: `averageCosting[${index}][${chIndex}].pricePerUnit`,
      ERROR_QUANTITY: `averageCosting[${index}][${chIndex}][${exIndex}].quantityPerSizCol`,
    },
    DETAILED: {
      ERROR_PRICE_PER_UNIT: `detailedCosting[${index}][${chIndex}][${exIndex}].pricePerUnit`,
      ERROR_QUANTITY: `detailedCosting[${index}][${chIndex}][${exIndex}].quantityPerSizCol`,
      ERROR_UOM: `detailedCosting[${index}][${chIndex}].quantityUnitOfMeasure`,
    },
    QUANTITIES: {
      ERROR_QUANTITY: `quantities[${index}][${chIndex}].quantityPerSizCol`,
    },
    PRODUCTINFO: {
      ERROR_SUPPLIER: 'productInfo.supplierId',
      ERROR_INCOTERMS: 'productInfo.incoterms',
      ERROR_CURRENCY: 'productInfo.currency',
      ERROR_TARGETCOST: 'productInfo.currency',
      ERROR_FACTORY: 'productInfo.factoryId',
    },
    SELECT_TEMPLATE: 'productTemplate',

    LANDED: {
      ERROR_COLOURWAY: `landedInfo.[${index}].colourway`,
      ERROR_SIZE: `landedInfo.[${index}].size`,
      ERROR_LENGTH: `landedInfo.[${index}].length`,
      ERROR_LANDED_COST: `landedInfo.[${index}].landedCost`,
      ERROR_COL_VALUE: `landedInfo.[${index}][${chIndex}].colValue`,
      ERROR_SELLINGPRICE: `landedInfo.[${index}].sellingPrice`,
    },
    TESTING: {
      ERROR_COLOURWAY: `testing.[${index}].colourway`,
      ERROR_TOTPRICE: `testing.[${index}].totalPrice`,
      ERROR_PRICEPERPRICE: `testing.[${index}].pricePerUnit`,
    },
    ADDITIONAL: {
      ERROR_COLOURWAY: `additional.[${index}].colourway`,
      ERROR_TITLE: `additional.[${index}].title`,
      ERROR_QUANTITY: `additional.[${index}].quantityPerColSizLen`,
      ERROR_QUANTITY_UOM: `additional.[${index}].quantityUom`,
      ERROR_TOTAL_QUANTITY_UOM: `additional.[${index}].totalQuantityUom`,
      ERROR_PRICEPER_UNIT: `additional.[${index}].pricePerUoM`,
    },
    ADDITIONAL_COMPONENT: {
      ERROR_COLOURWAY: `additionalComponent.[${index}].colourway`,
      ERROR_TITLE: `additionalComponent.[${index}].title`,
      ERROR_QUANTITY: `additionalComponent.[${index}].quantityPerColSizLen`,
      ERROR_QUANTITY_UOM: `additionalComponent.[${index}].quantityUom`,
      ERROR_TOTAL_QUANTITY_UOM: `additionalComponent.[${index}].totalQuantityUom`,
      ERROR_PRICEPER_UNIT: `additionalComponent.[${index}].pricePerUoM`,
    },
  };
};

export const additionalComponentFieldPath = (index?: RowIndex) => {
  return {
    SELECTED_TEMP_ADDTIONAL_DATA: 'additionalComponent.additionalComponentInfo',
    SELECTED_COLOURWAY: `additionalComponent.additionalComponentInfo[${index}].colourway`,
    UOM_ONE_SELECTED: `additionalComponent.additionalComponentInfo[${index}].quantityUom`,
    UOM_TWO_SELECTED: `additionalComponent.additionalComponentInfo[${index}].totalQuantityUom`,
    TOTAL_PRICE: `additionalComponent.additionalComponentInfo[${index}].totalPrice`,
    TOTAL_QUANTITY: `additionalComponent.additionalComponentInfo[${index}].totalQuantity`,
    QUANTITY_PER_UNIT: `additionalComponent.additionalComponentInfo[${index}].quantityPerColSizLen`,
    PRICE_PER_UNIT: `additionalComponent.additionalComponentInfo[${index}].pricePerUoM`,
    ITEM: `additionalComponent.additionalComponentInfo[${index}].title`,
    IS_CHANGED_BY_SUPPLIER_COLOURWAY: `additionalComponent.additionalComponentInfo[${index}].isChangedBySupplierColourway`,
    IS_CHANGED_BY_SUPPLIER_QUANTITY_UOM: `additionalComponent.additionalComponentInfo[${index}].isChangedBySupplierQuantityUom`,
    IS_CHANGED_BY_SUPPLIER_TOTAL_QUANTITY_UOM: `additionalComponent.additionalComponentInfo[${index}].isChangedBySupplierTotalQuantityUom`,
    IS_CHANGED_BY_SUPPLIER_TOTAL_PRICE: `additionalComponent.additionalComponentInfo[${index}].isChangedBySupplierTotalPrice`,
    IS_CHANGED_BY_SUPPLIER_TOTAL_QUANTITY: `additionalComponent.additionalComponentInfo[${index}].isChangedBySupplierTotalQuantity`,
    IS_CHANGED_BY_SUPPLIER_QUANTITY_PER_SIZE_COl: `additionalComponent.additionalComponentInfo[${index}].isChangedBySupplierTotalQuantityPerSizColLen`,
    IS_CHANGED_BY_SUPPLIER_PRICE_PER_UOM: `additionalComponent.additionalComponentInfo[${index}].isChangedBySupplierPricePerUom`,
    IS_CHANGED_BY_SUPPLIER_TITLE: `additionalComponent.additionalComponentInfo[${index}].isChangedBySupplierTitle`,
    IS_CHANGED_BY_SUPPLIER_PRICE_PER_UNIT: `additionalComponent.additionalComponentInfo[${index}].isChangedBySupplierPricePerUnit`,
    IS_EXCLUDED: `additionalComponent.additionalComponentInfo[${index}].isExcluded`,
    SUM_TOTAL_PRICE: 'additionalComponent.sumOfTotPrice',
  };
};

/** Lodash paths into the Specification Requirements slice (BM-9899). */
export const specificationRequirementFieldPath = (index?: RowIndex) => {
  return {
    ROWS: 'specificationRequirement.specificationRequirementInfo',
    REQUIREMENT: `specificationRequirement.specificationRequirementInfo[${index}].requirement`,
    SPECIFICATION: `specificationRequirement.specificationRequirementInfo[${index}].specification`,
    IS_FROM_TEMPLATE: `specificationRequirement.specificationRequirementInfo[${index}].isFromTemplate`,
  };
};
